import * as Sentry from '@sentry/vue';
import { Integrations } from '@sentry/tracing';
// import type { NavigationGuardNext } from 'vue-router';
import type { InitMonitorParams } from './utils/types';
import { getNetWorkSituation } from './libs/network';
import { initComponentCapture } from './libs/components';
import { initPageCapture } from './libs/pages';
import { initWebVitals } from './libs/web-vitals';
import { initWhiteScreen } from './libs/whtie-screen';

/**
 * @description 初始化监控配置
 */
const init = (params: InitMonitorParams) => {
    const { nuxtApp, dsn, environment, tracingOrigins, release, ignoreErrors, whiteScreen } = params;
    const { vueApp } = nuxtApp;
    Sentry.init({
        app: [vueApp],
        environment,
        dsn,
        // 解决可能造成的上报数据包太大的413报错 https://github.com/getsentry/sentry-javascript/issues/2798
        beforeBreadcrumb(breadcrumb, hint) {
            if (breadcrumb.category === 'console') {
                return null;
            }
            return breadcrumb;
        },
        integrations: [
            new Integrations.BrowserTracing({
                routingInstrumentation: Sentry.vueRouterInstrumentation(nuxtApp.$router),
                tracingOrigins,
            }),
        ],
        logErrors: true,
        attachProps: true,
        tracingOptions: {
            trackComponents: true,
        },
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,
        trackComponents: true,
        release,
        // 忽略无意义的错误
        ignoreErrors: [
            // Random plugins/extensions
            'top.GLOBALS',
            // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
            'originalCreateNotification',
            'canvas.contentDocument',
            'ResizeObserver loop limitexceeded',
            'ResizeObserver loop limit exceeded',
            'apis.google.com/',
            /.+__gCrWeb.learningToolsRuntimeBridge.raiseMessageFromHost.*/g, // 该错误由 chrome 引起
            ...(ignoreErrors || []),
        ],
        denyUrls: [/extensions\//i, /^chrome:\/\//i],
        beforeSend(event, hint) {
            // eslint-disable-next-line
            return new Promise(async (resolve, reject) => {
                const networkSituation = await getNetWorkSituation();
                event.extra = { ...(event.extra ?? {}), ...(networkSituation || {}) };
                resolve(event);
            });
        },
    });

    initWebVitals(Sentry);
    initComponentCapture(vueApp, Sentry);
    initPageCapture(vueApp, Sentry);

    // 每次路由跳转，都会执行一次白屏检测及资源加载检测
    nuxtApp.$router.beforeEach((to: Record<string, any>, from: Record<string, any>, next: NavigationGuardNext) => {
        // 暂不开启
        // initResource(Sentry);
        initWhiteScreen(Sentry, {
            skeletonProject: whiteScreen?.skeletonProject ?? false,
            whiteBoxElements: whiteScreen.whiteBoxElements,
            blankList: whiteScreen?.blankPageRoutes ?? [],
        });
        next();
    });

    return Sentry;
};

const ltMonitor = {
    init,
};

export default ltMonitor;
